import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import type { JSX } from 'react';

import { Skeleton } from '@/components/ui/skeleton.js';
import { AutomationDetail } from '@/features/automations/AutomationDetail.js';
import { AutomationReadOnlyDetail } from '@/features/automations/AutomationReadOnlyDetail.js';
import { ensureAutomationsEnabled } from '@/features/automations/guard.js';
import { automationProjectionQueryOptions } from '@/lib/api/automations.js';

export const Route = createFileRoute('/scheduled/$id')({
  component: AutomationDetailPage,
  beforeLoad: () => ensureAutomationsEnabled(),
});

function AutomationDetailPage(): JSX.Element {
  const { id } = useParams({ from: '/scheduled/$id' });
  const { data: automation, isLoading } = useQuery({
    ...automationProjectionQueryOptions(id),
    notifyOnChangeProps: ['data', 'isLoading', 'dataUpdatedAt'],
  });

  if (isLoading) {
    return (
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-4 px-4 py-6 md:px-6">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  if (!automation) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-20">
        <div className="flex max-w-sm flex-col items-center gap-6 text-center">
          <h1 className="font-heading text-xl font-bold tracking-tight text-foreground">
            Not found
          </h1>
          <p className="text-base text-muted-foreground">
            It may have been deleted, or you don't have access to it.
          </p>
          <Link
            to="/scheduled"
            className="inline-flex h-10 items-center gap-2 rounded-full border border-primary/40 bg-transparent px-5 text-sm font-medium text-primary transition-all hover:border-primary hover:bg-primary hover:text-primary-foreground"
          >
            Back to Scheduled
          </Link>
        </div>
      </div>
    );
  }

  /**
   * Keyed by the automation, not by the route: only `$id` changes when the reader moves from one
   * automation to the next, so the same editor instance would otherwise keep its unsaved drafts and
   * offer to save one automation's name and prompt under the other's id.
   */
  return automation.canManage ? (
    <AutomationDetail key={automation.id} automation={automation} />
  ) : (
    <AutomationReadOnlyDetail key={automation.id} automation={automation} />
  );
}
